import { z } from "zod";
import { catalogueLocale } from "../catalog/vocabulary";
import { calendarDay } from "./profile";
/**
 * `GET /me/retrospective?week=2026-W37&locale=pt` — the Retrospectiva's six
 * story cards for one ISO week (plan D36, T34).
 *
 * ── THE DEFINITIONS LIVE IN ONE PLACE ───────────────────────────────────────
 * `packages/modules/training/src/core/model/retrospective.ts`, built on
 * `progress.ts`'s week figures. In short:
 * - the week is an ISO-8601 week, Monday to Sunday UTC — the Progress week;
 *   absent, it is the last week whose Sunday has ended;
 * - `ready` is derived, never stored: the week has ended AND holds at least
 *   one ended session. `cards` is `null` whenever `ready` is false, a future
 *   week included (that is not an error);
 * - `streakWeeks` is the person's streak as of the week's end, and the
 *   streak counts PLAN-ANCHORED weeks (`stats.ts`), not ISO ones.
 *
 * ── A MONTH AND A YEAR (`D204`) ─────────────────────────────────────────────
 * `?period=month|year` tells the same story over a UTC calendar month or
 * year (`retrospective-period.ts`), the last ENDED one by default, and the
 * answer names it with `month` or `year` instead of `week`. Its muscles carry
 * `targetSets: null`: the plan's target is weekly.
 *
 * ── KEYS AND NAMES, NEVER SENTENCES ─────────────────────────────────────────
 * Every string here is an id or a localised catalogue name. The cards'
 * sentences are the client's copy (`D27`).
 */
/** `YYYY-Www`. The shape only: the week NUMBER (01–53, and 53 only in a year
 *  that has it) is checked by the server, as `retrospective.week_invalid`. */
export const isoWeek = z.string().regex(/^\d{4}-W\d{2}$/);
/**
 * The story's span (`D204`). `week` is the original six cards' and the
 * default; `month` and `year` are the same story over a calendar month or
 * year, UTC, like the week.
 */
export const RETROSPECTIVE_PERIODS = ["week", "month", "year"];
export const retrospectivePeriod = z.enum(RETROSPECTIVE_PERIODS);
/** `YYYY-MM`. The month NUMBER is checked by the server. */
export const calendarMonth = z.string().regex(/^\d{4}-\d{2}$/);
/** `YYYY`. */
export const calendarYear = z.string().regex(/^\d{4}$/);
export const retrospectiveQuery = z.object({
    /** Absent means `week`. An app that predates it never sends it. */
    period: retrospectivePeriod.optional(),
    /** Absent means the last complete ISO week. Read only for `week`. */
    week: isoWeek.optional(),
    /** Absent means the last complete month. Read only for `month`. */
    month: calendarMonth.optional(),
    /** Absent means the last complete year. Read only for `year`. */
    year: calendarYear.optional(),
    /** The locale muscle and exercise names are in. English when absent. */
    locale: catalogueLocale.optional(),
});
export const retrospectiveTopLift = z.object({
    exerciseId: z.string().min(1),
    name: z.string().min(1),
    /** The week's best Epley estimate for the lift. */
    e1rmKg: z.number().nonnegative(),
    /** The rise against the best estimate from before the week; `null` when no
     *  lift of the week had one (the lift is then the week's heaviest). */
    deltaKg: z.number().nullable(),
});
export const retrospectiveMuscle = z.object({
    muscleId: z.int().positive(),
    name: z.string().min(1),
    /** The week's sets whose exercise TARGETS the muscle. */
    sets: z.int().nonnegative(),
    /**
     * The weekly target: Σ `targetSets` over the ACTIVE plan's lines whose
     * exercise targets this muscle — the same rule `progress.ts`'s
     * `ProgressMuscle.targetSets` states, so the two screens never disagree
     * about what "na meta" means.
     *
     * `null`: no active plan, so there is no target to be at. ABSENT: a server
     * older than the field, and the client then counts nothing.
     *
     * When any muscle here carries a number, `muscles` also lists the muscles
     * that HAVE a target and 0 sets — otherwise "3 de 5 músculos na meta" would
     * be counted over a list the missed muscles are absent from.
     */
    targetSets: z.int().nonnegative().nullable().optional(),
});
/** The heaviest week the person has had up to AND including this one. A tie
 *  goes to the EARLIER week, so the answer stops moving once it is set. */
export const retrospectiveBestWeek = z.object({
    week: isoWeek,
    volumeKg: z.number().nonnegative(),
});
/** One of the period's most-trained exercises (`D204`). */
export const retrospectiveExercise = z.object({
    exerciseId: z.string().min(1),
    name: z.string().min(1),
    /** Sessions of the period with a working set of it. */
    sessions: z.int().nonnegative(),
    /** Its working sets in the period — warm-ups out. */
    sets: z.int().nonnegative(),
});
/** One UTC day of the period with an ended session (`D204`). */
export const retrospectiveDay = z.object({
    day: calendarDay,
    /** Workouts started that day — an abandoned one is not one (`D177`). */
    sessions: z.int().nonnegative(),
    volumeKg: z.number().nonnegative(),
});
export const retrospectiveCards = z.object({
    sessions: z.int().nonnegative(),
    minutes: z.int().nonnegative(),
    volumeKg: z.number().nonnegative(),
    /** Against the previous week; `null` when that week had no volume. */
    volumeDeltaPct: z.number().nullable(),
    topLift: retrospectiveTopLift.nullable(),
    streakWeeks: z.int().nonnegative(),
    /** Sorted by sets, descending. */
    muscles: z.array(retrospectiveMuscle),
    personalRecords: z.int().nonnegative(),
    /** The best week so far, for *"your heaviest week yet"*. `null`: no week up
     *  to this one has any volume. ABSENT: a server older than the field. */
    bestWeek: retrospectiveBestWeek.nullable().optional(),
    /** At most five, most sessions first (`D204`). ABSENT: an older server. */
    topExercises: z.array(retrospectiveExercise).optional(),
    /** The days of the period with an ended session, oldest first (`D204`).
     *  ABSENT: an older server. */
    calendar: z.array(retrospectiveDay).optional(),
});
export const retrospectiveOutput = z.object({
    /** ABSENT: an older server, which only ever answered a week. */
    period: retrospectivePeriod.optional(),
    /**
     * The ISO week, for a week's story. OPTIONAL since `D204` only because a
     * month and a year have no week: the server always sends it for a week, so
     * an app that predates the periods — which asks for nothing else — always
     * finds it.
     */
    week: isoWeek.optional(),
    /** `YYYY-MM`, for `period: "month"`. */
    month: calendarMonth.optional(),
    /** `YYYY`, for `period: "year"`. */
    year: calendarYear.optional(),
    /** The week has ended AND has at least one ended session. */
    ready: z.boolean(),
    /** `null` when `ready` is false. */
    cards: retrospectiveCards.nullable(),
});
//# sourceMappingURL=retrospective.js.map